import { useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CommentComposer, CommentItem, type ThreadComment } from '@/components/comments'
import { withContextTags, type NewComment } from '@/components/comment-tags'
import { useAuth } from '@/hooks/use-auth'
import {
  useCreateInvoiceComment,
  useDeleteInvoiceComment,
  useInvoiceComments,
  useUpdateInvoiceComment,
} from '@/features/invoices/data/comments'
import type { ApInvoiceAction } from '@/features/invoices/view-models'
import { formatStatus } from '@/lib/utils'
import type { InvoiceNotesContext } from './notes-context'

/** On the invoice, its own tag and its POs' tags go without saying. */
const INVOICE_TAGS: ReadonlySet<string> = new Set(['ap_invoice', 'purchase_order'])
const ACTION_CARD_TAGS: ReadonlySet<string> = new Set(['ap_invoice', 'purchase_order', 'action_item'])

function NoteList({
  notes,
  context,
  omitTypes,
}: {
  notes: ThreadComment[]
  context: InvoiceNotesContext
  omitTypes: ReadonlySet<string>
}) {
  const { user } = useAuth()
  const updateNote = useUpdateInvoiceComment(context.invoiceUuid)
  const deleteNote = useDeleteInvoiceComment(context.invoiceUuid)
  return (
    <div className="space-y-3" role="list">
      {notes.map((note) => (
        <div role="listitem" key={note.uuid}>
          <CommentItem
            comment={note}
            currentUserEmail={user?.email}
            projectUuid={context.projectUuid}
            omitTypes={omitTypes}
            saving={updateNote.isPending && updateNote.variables?.commentUuid === note.uuid}
            deleting={deleteNote.isPending && deleteNote.variables === note.uuid}
            onSave={(content, done) =>
              updateNote.mutate({ commentUuid: note.uuid, content }, { onSuccess: done })
            }
            onDelete={() => deleteNote.mutate(note.uuid)}
          />
        </div>
      ))}
    </div>
  )
}

/** Notes right under the invoice: what should change on it (tax, discount, free freight) and who said so. */
export function InvoiceNotes({ context }: { context: InvoiceNotesContext }) {
  const { data: comments, isError } = useInvoiceComments(context.invoiceUuid)
  // Comments on an action item show under its card.
  const notes = comments?.filter((note) => !note.mentions.some((mention) => mention.entity_type === 'action_item'))
  const createNote = useCreateInvoiceComment(context.invoiceUuid)
  const submit = (note: NewComment, clear: () => void) =>
    createNote.mutate(withContextTags(note, context.tags), { onSuccess: clear })
  return (
    <section aria-labelledby="invoice-notes-heading" className="space-y-3 rounded-xl border bg-card p-4">
      <h2 id="invoice-notes-heading" className="text-sm font-semibold">
        Notes
      </h2>
      <CommentComposer
        projectUuid={context.projectUuid}
        onSubmit={submit}
        pending={createNote.isPending}
        placeholder="What needs to change on this invoice? Use @ to mention someone"
      />
      {createNote.isError && <p className="text-xs text-destructive">Note not saved. Try again.</p>}
      {isError && <p className="text-xs text-destructive">Notes didn't load.</p>}
      {!!notes?.length && <NoteList notes={notes} context={context} omitTypes={INVOICE_TAGS} />}
    </section>
  )
}

/** The item's short name, as its tag reads in the invoice's, PO's and project's comments. */
function actionTagText(action: ApInvoiceAction) {
  const kind = action.card?.kind_label ?? formatStatus(action.action_type)
  return action.card?.sublabel ? `${kind} · ${action.card.sublabel}` : kind
}

/** Under an invoice action item card: its comments and a comment box, like a PO's action items (USA-1443). */
export function InvoiceActionNotes({
  action,
  context,
  canComment,
}: {
  action: ApInvoiceAction
  context: InvoiceNotesContext
  canComment: boolean
}) {
  const [composing, setComposing] = useState(false)
  const { data: notes } = useInvoiceComments(context.invoiceUuid)
  const createNote = useCreateInvoiceComment(context.invoiceUuid)
  const own = (notes ?? [])
    .filter((note) =>
      note.mentions.some(
        (mention) => mention.entity_type === 'action_item' && mention.entity_uuid === action.uuid,
      ),
    )
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
  if (!canComment && own.length === 0) return null
  const tags = [
    ...context.tags,
    { entity_type: 'action_item', entity_uuid: action.uuid, display_text: actionTagText(action) },
  ]
  return (
    <div className="space-y-3 border-t pt-3">
      {own.length > 0 && (
        <div aria-label="Comments on this action item">
          <NoteList notes={own} context={context} omitTypes={ACTION_CARD_TAGS} />
        </div>
      )}
      {composing ? (
        <CommentComposer
          projectUuid={context.projectUuid}
          pending={createNote.isPending}
          placeholder="Comment on this action item… Use @ to mention someone"
          onSubmit={(note, clear) =>
            createNote.mutate(withContextTags(note, tags), {
              onSuccess: () => {
                clear()
                setComposing(false)
              },
            })
          }
        />
      ) : (
        canComment && (
          <Button size="sm" variant="ghost" onClick={() => setComposing(true)}>
            <MessageSquare className="mr-1 h-3 w-3" aria-hidden /> Comment
          </Button>
        )
      )}
    </div>
  )
}
